import { useCallback } from "react";
import { useIntl } from "react-intl";

import { SearchOption } from "@components/fieldsNative/SearchInput";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import {
  GetEmployerBenefitsSearchDocument,
  GetEmployerBenefitsSearchQuery,
  GetEmployerBenefitsSearchQueryVariables
} from "@typing/Generated";

const useSearchEmployerBenefits = (journeyId: string, onClick: (id: string, title: string) => void) => {
  const intl = useIntl();
  const getEmployerBenefitsSearch = useLazyQueryWithPromise<
    GetEmployerBenefitsSearchQuery,
    GetEmployerBenefitsSearchQueryVariables
  >(GetEmployerBenefitsSearchDocument);

  const searchOptions = useCallback(
    (v: string) =>
      getEmployerBenefitsSearch({
        variables: {
          count: 35,
          journeyId,
          search: v
        }
      }).then(res => {
        const nodes = (res.data.employerBenefitsSearch ?? []).map(node => ({
          categories: node.categories,
          label: node.vendorName,
          onClick: () => onClick(node.id, node.vendorName),
          value: node.id
        }));

        const final = nodes.reduce(
          (acc, node) => {
            node.categories.forEach(rawCategory => {
              const category = intl.formatMessage({ id: `models.employerBenefitCategory.${rawCategory}` });
              const existingCategory = acc.find(item => item.label === category);
              if (existingCategory) {
                existingCategory.options.push({
                  category,
                  ...node
                });
              } else {
                acc.push({
                  label: category,
                  options: [
                    {
                      category,
                      ...node
                    }
                  ]
                });
              }
            });
            return acc;
          },
          [] as { label: string; options: SearchOption[] }[]
        );

        return final.sort((a, b) => a.label.localeCompare(b.label));
      }),
    [getEmployerBenefitsSearch, intl, journeyId, onClick]
  );

  return searchOptions;
};

export default useSearchEmployerBenefits;
